import { TEXT } from "@/constants/text";
import type { PerformanceReportRow } from "@/types/reporting";
import {
  CSV_ESCAPE,
  fmt,
  fmtPct,
  fmtStr,
  integerFormatter,
} from "@/utils/tableFormatters";

const TABLE_TEXT = TEXT.campaignOverview.audienceSegment;

export const SEGMENT_FILTER_FIELDS = [
  "audience_parent_vendor_name",
  "audience_segment_name",
  "audience_vendor_name",
] as const;

export type SortOrder = 1 | -1;

const isEmpty = (value: unknown) => value == null || value === "";

// Sorts the way the table's own column sort does — empty values always last,
// text compared by locale — so an export matches what's on screen.
export const sortRowsByField = (
  rows: PerformanceReportRow[],
  field: keyof PerformanceReportRow,
  order: SortOrder
): PerformanceReportRow[] =>
  [...rows].sort((rowA, rowB) => {
    const valueA = rowA[field];
    const valueB = rowB[field];
    if (isEmpty(valueA) && isEmpty(valueB)) return 0;
    if (isEmpty(valueA)) return 1;
    if (isEmpty(valueB)) return -1;
    if (typeof valueA === "string" && typeof valueB === "string") {
      return valueA.localeCompare(valueB) * order;
    }
    return (valueA < valueB ? -1 : Number(valueA > valueB)) * order;
  });

export const filterSegmentRows = (
  rows: PerformanceReportRow[],
  term: string
): PerformanceReportRow[] => {
  const needle = term.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((row) =>
    SEGMENT_FILTER_FIELDS.some((field) => {
      const value = row[field];
      return typeof value === "string" && value.toLowerCase().includes(needle);
    })
  );
};

export const buildSegmentCsv = (rows: PerformanceReportRow[]): string => {
  const headers = [
    TABLE_TEXT.columnSegmentName,
    TABLE_TEXT.columnParentVendor,
    TABLE_TEXT.columnVendor,
    TABLE_TEXT.columnImpressions,
    TABLE_TEXT.columnClicks,
    TABLE_TEXT.columnCtr,
    TABLE_TEXT.columnVcr,
  ].join(",");

  const body = rows
    .map((row) =>
      [
        CSV_ESCAPE(fmtStr(row.audience_segment_name)),
        CSV_ESCAPE(fmtStr(row.audience_parent_vendor_name)),
        CSV_ESCAPE(fmtStr(row.audience_vendor_name)),
        CSV_ESCAPE(fmt(row.impressions, integerFormatter)),
        CSV_ESCAPE(fmt(row.clicks, integerFormatter)),
        CSV_ESCAPE(fmtPct(row.ctr)),
        CSV_ESCAPE(fmtPct(row.video_complete_rate)),
      ].join(",")
    )
    .join("\n");

  return `${headers}\n${body}`;
};
